'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { PageHeader } from '@/components/common/page-header';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { useOrganization } from '@/contexts/organization-context';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import {
  Dealcard,
  PetrohqApiResponseListDealcard,
} from '@/lib/api/data-contracts';
import { useFilterStore } from '@/stores/filter-store';
import { useCallback, useEffect, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';

export function CustCardManagement() {
  const { toast } = useToast();
  const [dealcards, setDealcards] = useState<Dealcard[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 필터 상태 가져오기
  const { store } = useFilterStore();
  const { getAllStores } = useOrganization();

  const fetchDealcards = useCallback(async () => {
    setIsLoading(true);
    try {
      // 필터에서 선택된 store 값들을 사용, 없으면 모든 store 사용
      const storeCodeList = store.length > 0 ? store : getAllStores();

      const searchParams = new URLSearchParams();
      storeCodeList.forEach(storeCode => {
        searchParams.append('storeCodeList', storeCode);
      });

      const response = await apiClient.get('master/cust-card', {
        searchParams: searchParams,
      });
      const data = (await response.json()) as PetrohqApiResponseListDealcard;
      const dealcardData = data.result || [];
      setDealcards(dealcardData);
    } catch {
      toast({
        title: '조회 실패',
        description: '고객카드 데이터를 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [store, toast, getAllStores]);

  // 초기 데이터 로드
  useEffect(() => {
    fetchDealcards();
  }, []);

  // 조회 버튼 클릭 핸들러
  const handleSearch = () => {
    fetchDealcards();
  };

  return (
    <ExpandableCard
      breadcrumb={
        <Breadcrumb items={menuBreadcrumbItems('cust-card')} />
      }
      title='고객카드 목록'
      fill
      pageHeader={
        <PageHeader
          onSearch={handleSearch}
          isLoading={isLoading}
        />
      }
    >
      {/* 고객카드 테이블 */}
      <div
        className='border rounded-lg flex-1 min-h-0'
      >
        <div className='w-full h-full'>
          <RealGridReact
            gridProps={{
              checkBar: { visible: false },
              stateBar: { visible: false },
              edit: { readOnly: true, editable: false },
              sorting: {
                enabled: true,
                showSortOrder: true,
                style: RealGrid.SortStyle.INCLUSIVE,
              },
            }}
            rows={dealcards}
          >
            <RGDataField
              fieldName={'compCd'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'storeCd'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'cardNo'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'cardBnus'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'dealCode'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'carNo'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'cardCheck'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataColumn fieldName={'storeCd'} header={{ text: '매장' }} />
            <RGDataColumn
              fieldName={'cardNo'}
              header={{ text: '카드번호' }}
              width={60}
            />
            <RGDataColumn
              fieldName={'cardBnus'}
              header={{ text: '보너스카드' }}
              width={110}
            />
            <RGDataColumn
              fieldName={'dealCode'}
              header={{ text: '거래처코드' }}
              width={70}
            />
            <RGDataColumn fieldName={'carNo'} header={{ text: '차량번호' }} />
            <RGDataColumn
              fieldName={'cardCheck'}
              header={{ text: '정지카드여부' }}
              width={80}
            />
          </RealGridReact>
        </div>
      </div>
    </ExpandableCard>
  );
}
